import { AlertCircle, CheckCircle2, Loader2, MailWarning } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  type EmailPreferenceDetails,
  type EmailPreferencePurpose,
  EmailPreferenceRequestError,
  emailPreferencesApi,
} from '@/api/email-preferences';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface EmailPreferencePageProps {
  purpose: EmailPreferencePurpose;
}

interface PreferencePageError {
  message: string;
  invalidLink: boolean;
}

function toPreferencePageError(error: unknown, fallbackMessage: string): PreferencePageError {
  if (error instanceof EmailPreferenceRequestError) {
    return {
      message: error.message,
      invalidLink: error.code === 'EMAIL_PREFERENCE_TOKEN_INVALID',
    };
  }

  return {
    message: error instanceof Error ? error.message : fallbackMessage,
    invalidLink: false,
  };
}

function pageCopy(purpose: EmailPreferencePurpose) {
  return purpose === 'unsubscribe'
    ? {
        title: 'Stop monitoring emails',
        description: 'Choose whether to stop monitoring emails for this address.',
        action: 'Stop monitoring emails',
        working: 'Stopping monitoring emails...',
        complete: 'Monitoring emails stopped',
        unchanged: 'Monitoring emails are already stopped',
        explanation:
          'Outage, reminder, and recovery emails from this organization will stop. Monitoring will continue for other recipients and channels.',
      }
    : {
        title: 'Resume monitoring emails',
        description: 'Choose whether to resume monitoring emails for this address.',
        action: 'Resume monitoring emails',
        working: 'Resuming monitoring emails...',
        complete: 'Monitoring emails resumed',
        unchanged: 'Monitoring emails are already enabled',
        explanation:
          'Outage, reminder, and recovery emails from this organization will resume for this address.',
      };
}

function isAlreadyInRequestedState(details: EmailPreferenceDetails): boolean {
  return (
    (details.purpose === 'unsubscribe' && details.status === 'unsubscribed') ||
    (details.purpose === 'resubscribe' && details.status === 'subscribed')
  );
}

export function EmailPreferencePage({ purpose }: EmailPreferencePageProps) {
  const { token } = useParams();
  const copy = pageCopy(purpose);
  const [details, setDetails] = useState<EmailPreferenceDetails | null>(null);
  const [error, setError] = useState<PreferencePageError | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmationChanged, setConfirmationChanged] = useState<boolean | null>(null);
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load(_attempt: number) {
      if (!token) {
        setError({
          message: 'This email preference link is invalid or expired.',
          invalidLink: true,
        });
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      setConfirmationChanged(null);
      try {
        const result = await emailPreferencesApi.get(purpose, token);
        if (!cancelled) setDetails(result);
      } catch (err) {
        if (!cancelled) {
          setDetails(null);
          setError(toPreferencePageError(err, 'Unable to load this email preference link.'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load(reloadCount);
    return () => {
      cancelled = true;
    };
  }, [purpose, reloadCount, token]);

  const confirm = async () => {
    if (!token || !details || !details.canConfirm) return;

    setIsConfirming(true);
    setError(null);
    try {
      const result = await emailPreferencesApi.confirm(purpose, token);
      setDetails((current) =>
        current ? { ...current, status: result.status, canConfirm: false } : current,
      );
      setConfirmationChanged(result.changed);
    } catch (err) {
      setError(
        toPreferencePageError(err, 'Unable to update this email preference. Please try again.'),
      );
    } finally {
      setIsConfirming(false);
    }
  };

  const isComplete = details ? isAlreadyInRequestedState(details) : false;

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-12 sm:px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Valvo.io</h1>
          <p className="mt-2 text-sm text-slate-600">Manage monitoring email delivery</p>
        </div>
        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-2 pb-4 text-center">
            <CardTitle className="text-2xl font-semibold">{copy.title}</CardTitle>
            <CardDescription>{copy.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {isLoading ? (
              <div
                className="flex items-center justify-center gap-2 py-8 text-sm text-slate-600"
                aria-live="polite"
              >
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Loading email preference...
              </div>
            ) : error && !details ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>
                  {error.invalidLink
                    ? 'This link is invalid or expired'
                    : 'Unable to open this link'}
                </AlertTitle>
                <AlertDescription>
                  <p>{error.message}</p>
                  {!error.invalidLink && (
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-3"
                      onClick={() => setReloadCount((count) => count + 1)}
                    >
                      Retry
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            ) : details ? (
              <>
                <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-700">
                  <div className="font-medium text-slate-900">
                    {details.organizationName} · {details.maskedEmail}
                  </div>
                  <p className="mt-2 leading-6">{copy.explanation}</p>
                </div>

                {isComplete ? (
                  <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900">
                    <CheckCircle2 className="h-4 w-4 text-emerald-700" aria-hidden="true" />
                    <AlertTitle>
                      {confirmationChanged === false ? copy.unchanged : copy.complete}
                    </AlertTitle>
                    <AlertDescription>
                      {purpose === 'unsubscribe'
                        ? 'No further action is needed.'
                        : 'Future monitoring emails can be delivered to this address.'}
                    </AlertDescription>
                  </Alert>
                ) : details.canConfirm ? (
                  <Button
                    type="button"
                    className="w-full"
                    onClick={confirm}
                    disabled={isConfirming}
                  >
                    {isConfirming ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                        {copy.working}
                      </>
                    ) : (
                      copy.action
                    )}
                  </Button>
                ) : (
                  <Alert variant="destructive">
                    <MailWarning className="h-4 w-4" aria-hidden="true" />
                    <AlertTitle>This link cannot make that change</AlertTitle>
                    <AlertDescription>Please use a current confirmation link.</AlertDescription>
                  </Alert>
                )}

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" aria-hidden="true" />
                    <AlertDescription>{error.message}</AlertDescription>
                  </Alert>
                )}
              </>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
